import { Cross1Icon, HamburgerMenuIcon } from '@radix-ui/react-icons';
import { Box, IconButton, ScrollArea, Separator, Slot, Theme, Tooltip } from '@workos-inc/design-system';
import { useRouter } from 'next/router';
import { Dialog as DialogPrimitive } from 'radix-ui';
import * as React from 'react';
import { RemoveScroll } from 'react-remove-scroll';
import { NavigationItems } from './navigation-items';
import { Search } from './search';
const NavigationMobileContext = React.createContext<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
} | null>(null);
const useNavigationMobileContext = () => {
  const navigationMobileContext = React.useContext(NavigationMobileContext);
  if (!navigationMobileContext) {
    throw new TypeError('`useNavigationMobileContext` must be called within `NavigationMobileContext.Provider`');
  }
  return navigationMobileContext;
};
export const NavigationMobileProvider: React.FC<{
  children?: React.ReactNode;
}> = ({
  children
}) => {
  const [open, setOpen] = React.useState(false);
  return <DialogPrimitive.Root modal={false} open={open} onOpenChange={open => setOpen(open)} data-sentry-element="DialogPrimitive.Root" data-sentry-component="NavigationMobileProvider" data-sentry-source-file="navigation-mobile.tsx">
      <NavigationMobileContext.Provider value={{
      open,
      onOpenChange: setOpen
    }} data-sentry-element="NavigationMobileContext.Provider" data-sentry-source-file="navigation-mobile.tsx">
        {children}
      </NavigationMobileContext.Provider>
    </DialogPrimitive.Root>;
};
export const NavigationMobileToggle = () => {
  const context = useNavigationMobileContext();
  const [tooltipOpen, setTooltipOpen] = React.useState(false);
  const [deviceHasTouch, setDeviceHasTouch] = React.useState(false);

  // Prevent return focus triggering tooltip on touch devices
  React.useEffect(() => {
    setDeviceHasTouch(window.matchMedia('(hover: none)').matches);
  }, []);
  return <Box display={{
    md: 'none'
  }} data-sentry-element="Box" data-sentry-component="NavigationMobileToggle" data-sentry-source-file="navigation-mobile.tsx">
      <Tooltip align="end" content="Menu" open={!deviceHasTouch && tooltipOpen} onOpenChange={open => setTooltipOpen(open)} data-sentry-element="Tooltip" data-sentry-source-file="navigation-mobile.tsx">
        <DialogPrimitive.Trigger asChild data-sentry-element="DialogPrimitive.Trigger" data-sentry-source-file="navigation-mobile.tsx">
          <IconButton data-sentry-element="IconButton" data-sentry-source-file="navigation-mobile.tsx">
            {context.open ? <Cross1Icon height="16" width="16" /> : <HamburgerMenuIcon height="16" width="16" />}
          </IconButton>
        </DialogPrimitive.Trigger>
      </Tooltip>
    </Box>;
};
export const NavigationMobileMenu: React.FC<{
  children?: React.ReactNode;
}> = ({
  children
}) => <DialogPrimitive.Portal data-sentry-element="DialogPrimitive.Portal" data-sentry-component="NavigationMobileMenu" data-sentry-source-file="navigation-mobile.tsx">
    <Theme asChild data-sentry-element="Theme" data-sentry-source-file="navigation-mobile.tsx">
      <DialogPrimitive.Content className="NavigationMobileMenu" onInteractOutside={event => event.preventDefault()} onOpenAutoFocus={event => {
      // Introducing lint rule banning type assertions
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      const target = event.currentTarget as HTMLElement;
      target?.focus();
      event.preventDefault();
    }} data-sentry-element="DialogPrimitive.Content" data-sentry-source-file="navigation-mobile.tsx">
        <NavigationMobileMenuContent data-sentry-element="NavigationMobileMenuContent" data-sentry-source-file="navigation-mobile.tsx">{children}</NavigationMobileMenuContent>
      </DialogPrimitive.Content>
    </Theme>
  </DialogPrimitive.Portal>;
const NavigationMobileMenuContent: React.FC<{
  children?: React.ReactNode;
}> = ({
  children
}) => {
  const router = useRouter();
  const context = useNavigationMobileContext();
  const scrollViewportRef = React.useRef<HTMLDivElement>(null);
  const [searchOpen, setSearchOpen] = React.useState(false);

  // Close menu on item selection or route change
  const {
    onOpenChange
  } = context;
  React.useEffect(() => {
    const handleRouteChange = () => onOpenChange(false);
    router.events.on('routeChangeStart', handleRouteChange);
    return () => router.events.off('routeChangeStart', handleRouteChange);
  }, [router.events, onOpenChange]);
  const handleScrollToTop = React.useCallback(() => {
    if (scrollViewportRef.current) {
      scrollViewportRef.current.scrollTop = 0;
    }
  }, []);
  const handleSearchOpenChange = React.useCallback((isOpen: boolean) => setSearchOpen(isOpen), []);
  return <RemoveScroll as={Slot} data-sentry-element="RemoveScroll" data-sentry-component="NavigationMobileMenuContent" data-sentry-source-file="navigation-mobile.tsx">
      <ScrollArea.Root data-sentry-element="ScrollArea.Root" data-sentry-source-file="navigation-mobile.tsx">
        <ScrollArea.Viewport ref={scrollViewportRef} className="NavigationMobileScrollAreaViewport" data-sentry-element="ScrollArea.Viewport" data-sentry-source-file="navigation-mobile.tsx">
          <Box pb="9" data-sentry-element="Box" data-sentry-source-file="navigation-mobile.tsx">
            <Box pb="2" pt="4" px="4" data-sentry-element="Box" data-sentry-source-file="navigation-mobile.tsx">
              <Search variant="mobile" onEmptyEscapeKeyDown={() => context.onOpenChange(false)} onOpenChange={handleSearchOpenChange} onQueryChange={handleScrollToTop} data-sentry-element="Search" data-sentry-source-file="navigation-mobile.tsx" />
            </Box>

            {!searchOpen && <Box px="4">
                {children && <Box mt="3">
                    {children} <Separator mb="6" mt="4" size="4" />
                  </Box>}
                <Box mt="5">
                  <NavigationItems />
                </Box>
              </Box>}
          </Box>
        </ScrollArea.Viewport>
        <ScrollArea.Scrollbar my="4" orientation="vertical" style={{
        zIndex: 1
      }} data-sentry-element="ScrollArea.Scrollbar" data-sentry-source-file="navigation-mobile.tsx" />
      </ScrollArea.Root>
    </RemoveScroll>;
};